import React from 'react';
import { Link } from 'react-router';
import { AppShell, Badge, Burger, Group, Image, Menu, ScrollArea } from '@mantine/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logoMPA from '@/assets/logo-mpa.png';
import logoMessages from '@/assets/messages-logo.svg';
import logoMessagesCompact from '@/assets/messages-logo-compact.svg';
import logoMPACompact from '@/assets/logo-mpa_compact.svg';
import logoForms from '@/assets/logo-forms.svg';
import logoFormsCompact from '@/assets/logo-forms_compact.svg';
import logoMinistries from '@/assets/logo-ministries.svg';
import { CommunityIcon } from '@/assets';
import styles from './Navigation.module.sass';
import {
  IconCalendarMonth,
  IconChartArrowsVertical,
  IconClockHour5,
  IconHome,
  IconHourglassEmpty,
  IconInbox,
  IconInfoCircle,
  IconMessageCircle,
  IconPencil,
  IconPhotoScan,
  IconSend,
  IconSendOff,
  IconSettings,
  IconSettings2,
  IconSpeakerphone,
  IconTrash,
  IconUser,
  IconUsersGroup,
} from '@tabler/icons-react';
import { IS_COMMUNITY_ENABLED } from '@/configs/community';
import { Links, ROUTE_ALIASES } from '@/routing/routesAliases';
import { SupportPad } from '@/components/SupportPad/SupportPad';
import { UserMenu } from '@/components/UserMenu/UserMenu';
import { VisibleBetween } from '@/components/VisibleBetween/VisibleBetween';
import {
  LIMITED_CLUSTER_SUB_NAV_KEYS,
  appendLimitedClusterSubQueryToPath,
  getLimitedScopeAllowedMainNavKeys,
  getWebPartition,
  hasCommunityAccess,
  isLimitedAdminScope,
  isNavItemVisibleForPermissions,
  isRestrictedGroupAdminScope,
} from '@/helpers';
import {
  useChurch,
  useEnhancedPackagesConfig,
  useGlobalSettings,
  useIsLimitedClusterSubView,
  useScreenSize,
} from '@/hooks';
import classNames from 'classnames';
import { MESSAGE_STATUS, WEB_PARTITION } from '@/types';

type NavItem = {
  key: string;
  path: string;
  labelKey: string;
  counter?: number;
  icon?: React.ReactNode;
  activeMatchPrefixes?: string[];
};

const myParishNavItems: NavItem[] = [
  { key: 'home', path: ROUTE_ALIASES.HOME, labelKey: 'navigation.home', icon: <IconHome /> },
  { key: 'groups', path: ROUTE_ALIASES.GROUPS, labelKey: 'navigation.groups', icon: <IconUsersGroup /> },
  { key: 'members', path: ROUTE_ALIASES.MEMBERS, labelKey: 'navigation.members', icon: <IconUser /> },
  {
    key: 'calendar',
    path: ROUTE_ALIASES.CALENDAR,
    labelKey: 'navigation.calendar',
    icon: <IconCalendarMonth />,
  },
  {
    key: 'parish-info',
    path: ROUTE_ALIASES.PARISH_INFO_ROOT,
    labelKey: 'navigation.parishInfo',
    icon: <IconInfoCircle />,
    activeMatchPrefixes: [ROUTE_ALIASES.PARISH_DETAILS_ROOT],
  },
  { key: 'app-setup', path: ROUTE_ALIASES.APP_SETUP_ROOT, labelKey: 'navigation.appSetup', icon: <IconSettings2 /> },
  {
    key: 'community',
    path: Links.getCommunityRoute(),
    labelKey: 'navigation.community',
    icon: <CommunityIcon />,
    activeMatchPrefixes: [ROUTE_ALIASES.COMMUNITY],
  },
];

const messagesNavMainItems: NavItem[] = [
  { key: 'inbox', path: ROUTE_ALIASES.MESSAGES_INBOX, labelKey: 'navigation.messages.inbox', icon: <IconInbox /> },
  {
    key: 'announcements',
    path: ROUTE_ALIASES.MESSAGES_ANNOUNCEMENTS,
    labelKey: 'navigation.messages.announcements',
    icon: <IconSpeakerphone />,
  },
  {
    key: 'Templates',
    path: ROUTE_ALIASES.MESSAGES_TEMPLATES,
    labelKey: 'navigation.messages.templates',
    icon: <IconPhotoScan />,
  },
  {
    key: 'Scheduled',
    path: ROUTE_ALIASES.MESSAGES_SCHEDULED,
    labelKey: 'navigation.messages.scheduled',
    icon: <IconClockHour5 />,
  },
  {
    key: 'textStatus',
    path: ROUTE_ALIASES.MESSAGES_TEXT_STATUS,
    labelKey: 'navigation.messages.textStatus',
    icon: <IconMessageCircle />,
  },
];

const messagesNavHiddenItems: NavItem[] = [
  { key: 'sent', path: ROUTE_ALIASES.MESSAGES_SENT, labelKey: 'navigation.messages.sent', icon: <IconSend /> },
  {
    key: 'Drafts',
    path: ROUTE_ALIASES.MESSAGES_DRAFTS,
    labelKey: 'navigation.messages.drafts',
    icon: <IconPencil />,
  },
  {
    key: 'Deleted',
    path: ROUTE_ALIASES.MESSAGES_DELETED,
    labelKey: 'navigation.messages.deleted',
    icon: <IconTrash />,
  },
];

const messagesNavItems: NavItem[] = [...messagesNavMainItems, ...messagesNavHiddenItems];

const formsNavItems: NavItem[] = [
  {
    key: 'published',
    path: ROUTE_ALIASES.FORMS_PUBLISHED,
    labelKey: 'navigation.forms.published',
    icon: <IconSend />,
  },
  {
    key: 'unpublished',
    path: ROUTE_ALIASES.FORMS_UNPUBLISHED,
    labelKey: 'navigation.forms.unpublished',
    icon: <IconSendOff />,
  },
  {
    key: 'Drafts',
    path: ROUTE_ALIASES.FORMS_DRAFTS,
    labelKey: 'navigation.forms.drafts',
    icon: <IconPencil />,
  },
  {
    key: 'Templates',
    path: ROUTE_ALIASES.FORMS_TEMPLATES,
    labelKey: 'navigation.forms.templates',
    icon: <IconPhotoScan />,
  },
];

const ministriesNavItems: NavItem[] = [
  { key: 'home', path: ROUTE_ALIASES.MINISTRIES, labelKey: 'navigation.ministries.home', icon: <IconHome /> },
  {
    key: 'calendar-view',
    path: ROUTE_ALIASES.MINISTRIES_CALENDAR_VIEW,
    labelKey: 'navigation.ministries.calendarView',
    icon: <IconHourglassEmpty />,
  },
  {
    key: 'scheduling',
    path: ROUTE_ALIASES.MINISTRIES_SCHEDULING,
    labelKey: 'navigation.ministries.scheduling',
    icon: <IconHourglassEmpty />,
  },
  {
    key: 'group',
    path: ROUTE_ALIASES.MINISTRIES_GROUP,
    labelKey: 'navigation.ministries.group',
    icon: <IconUsersGroup />,
  },
  {
    key: 'volunteers',
    path: ROUTE_ALIASES.MINISTRIES_VOLUNTEERS,
    labelKey: 'navigation.ministries.volunteers',
    icon: <IconUser />,
  },
  {
    key: 'reports',
    path: ROUTE_ALIASES.MINISTRIES_REPORTS,
    labelKey: 'navigation.ministries.reports',
    icon: <IconChartArrowsVertical />,
  },
  {
    key: 'settings',
    path: ROUTE_ALIASES.MINISTRIES_SETTINGS,
    labelKey: 'navigation.ministries.settings',
    icon: <IconSettings />,
  },
];

const CONFIG: Record<
  WEB_PARTITION,
  {
    logoUrl: string;
    logoCompactUrl: string;
    navItems: NavItem[];
    rootUrl: string;
  }
> = {
  [WEB_PARTITION.MESSAGES]: {
    logoUrl: logoMessages,
    logoCompactUrl: logoMessagesCompact,
    navItems: messagesNavItems,
    rootUrl: ROUTE_ALIASES.MESSAGES,
  },
  [WEB_PARTITION.FORMS]: {
    logoUrl: logoForms,
    logoCompactUrl: logoFormsCompact,
    navItems: formsNavItems,
    rootUrl: ROUTE_ALIASES.FORMS,
  },
  [WEB_PARTITION.MINISTRIES]: {
    logoUrl: logoMinistries,
    logoCompactUrl: logoMinistries,
    navItems: ministriesNavItems,
    rootUrl: ROUTE_ALIASES.MINISTRIES,
  },
  [WEB_PARTITION.MPA]: {
    logoUrl: logoMPA,
    logoCompactUrl: logoMPACompact,
    navItems: myParishNavItems,
    rootUrl: ROUTE_ALIASES.HOME,
  },
};
type NavigationProps = {
  isMobileMenuOpened: boolean;
  isDesktopMenuExpanded: boolean;
  toggleMobileMenu: () => void;
  messagesCounters?: Partial<Record<MESSAGE_STATUS, number>>;
};

export const Navigation = ({
  isMobileMenuOpened,
  isDesktopMenuExpanded,
  toggleMobileMenu,
  messagesCounters,
}: NavigationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('global');
  const { isMobile, isDesktop } = useScreenSize();
  const { currentChurch } = useGlobalSettings();
  const { data: church } = useChurch(currentChurch?.churchId);
  const enhancedPackagesConfig = useEnhancedPackagesConfig();
  const isLimitedClusterSubView = useIsLimitedClusterSubView();
  const isDesktopCollapsed = Boolean(isDesktop) && !isDesktopMenuExpanded;

  const currentPartition: WEB_PARTITION = getWebPartition(location.pathname);
  const { logoUrl, logoCompactUrl, navItems, rootUrl } = CONFIG[currentPartition];

  const isRestrictedGroupAdmin = isRestrictedGroupAdminScope({
    scopeTypes: currentChurch?.scopeTypes,
    isGroupScope: currentChurch?.isGroupScope,
  });

  const messagesNavItemsFiltered = messagesNavItems.filter((item) => {
    if (item.key === 'textStatus' && (!church?.isSendSms || !enhancedPackagesConfig.isTextStatusVisible)) {
      return false;
    }

    if (item.key === 'announcements' && isRestrictedGroupAdmin) {
      return false;
    }

    return isNavItemVisibleForPermissions({
      navItemKey: item.key,
      permissions: currentChurch?.permissions,
    });
  });

  const visibleNavItems = navItems.filter((item) => {
    if (
      currentPartition === WEB_PARTITION.FORMS &&
      item.key === 'Templates' &&
      !enhancedPackagesConfig.isFormsTemplatesVisible
    ) {
      return false;
    }

    if (isLimitedClusterSubView && currentPartition === WEB_PARTITION.MPA) {
      return LIMITED_CLUSTER_SUB_NAV_KEYS.includes(item.key);
    }

    if (item.key === 'community') {
      return IS_COMMUNITY_ENABLED && hasCommunityAccess({ scopeTypes: currentChurch?.scopeTypes });
    }

    return isNavItemVisibleForPermissions({
      navItemKey: item.key,
      permissions: currentChurch?.permissions,
    });
  });

  const counters = {
    inbox: messagesCounters?.[MESSAGE_STATUS.INBOX],
    sent: undefined,
    Drafts: undefined,
    Deleted: undefined,
    Scheduled: undefined,
  };

  const isNavItemActive = (navItem: NavItem) => {
    if (location.pathname === navItem.path) {
      return true;
    }

    const isProductRootPath = navItem.path === ROUTE_ALIASES.HOME || navItem.path === ROUTE_ALIASES.MINISTRIES;
    if (!isProductRootPath && location.pathname.startsWith(navItem.path)) {
      return true;
    }

    if (navItem.activeMatchPrefixes?.some((prefix) => location.pathname.startsWith(prefix))) {
      return true;
    }

    return false;
  };

  const limitedScopeAllowedMainNavKeys = getLimitedScopeAllowedMainNavKeys({
    scopeTypes: currentChurch?.scopeTypes,
    isGroupScope: currentChurch?.isGroupScope,
  });
  const isLimitedScopeNavigation =
    currentPartition === WEB_PARTITION.MPA &&
    isLimitedAdminScope({
      isLimitedScope: currentChurch?.isLimitedScope,
      isGroupScope: currentChurch?.isGroupScope,
    });

  const renderNavItem = (navItem: NavItem) => {
    const { path, labelKey, icon, key } = navItem;
    const label = t(labelKey);
    const isMainNavItemDisabledForLimitedScope =
      isLimitedScopeNavigation && !limitedScopeAllowedMainNavKeys.includes(key);
    const count = counters[key as keyof typeof counters];
    const rightSection = (count ?? 0) > 0 && !isDesktopCollapsed && (
      <Badge color="brand" size="md" className={styles.badge}>
        {count}
      </Badge>
    );
    return (
      <Menu.Item
        key={key}
        leftSection={icon}
        rightSection={rightSection}
        disabled={isMainNavItemDisabledForLimitedScope}
        title={isDesktopCollapsed ? label : undefined}
        aria-label={isDesktopCollapsed ? label : undefined}
        onClick={() => {
          if (isMainNavItemDisabledForLimitedScope) {
            return;
          }
          navigate(appendLimitedClusterSubQueryToPath({ path, search: location.search }));
          if (isMobileMenuOpened && isMobile) {
            toggleMobileMenu();
          }
        }}
        className={classNames(styles.navigationLink, {
          [styles.active]: isNavItemActive(navItem),
        })}
      >
        {label}
      </Menu.Item>
    );
  };

  const isMessagesPartition = currentPartition === WEB_PARTITION.MESSAGES;

  return (
    <AppShell.Navbar px="md" className={classNames({ [styles.collapsed]: isDesktopCollapsed })}>
      <AppShell.Section
        className={styles.wrapper}
        style={{ justifyContent: isDesktopCollapsed ? 'center' : 'space-between' }}
      >
        <Group gap="xs" align="center">
          {!isDesktopCollapsed && (
            <div className={styles.userMenu}>
              <UserMenu />
            </div>
          )}
          <Link to={rootUrl} className={classNames(styles.logoWrapper, { [styles.compact]: isDesktopCollapsed })}>
            <Image src={isDesktopCollapsed ? logoCompactUrl : logoUrl} className={styles.logo} alt="myParish logo" />
          </Link>
        </Group>

        <VisibleBetween to="lg">
          <Burger opened={isMobileMenuOpened} onClick={toggleMobileMenu} size="sm" />
        </VisibleBetween>
      </AppShell.Section>
      <AppShell.Section grow my="md" component={ScrollArea} className={styles.menuWrapper}>
        <Menu withinPortal={false} trapFocus={false} menuItemTabIndex={0}>
          <div>
            {isMessagesPartition ? messagesNavItemsFiltered.map(renderNavItem) : visibleNavItems.map(renderNavItem)}
          </div>
          {!isDesktopCollapsed && <SupportPad />}
        </Menu>
      </AppShell.Section>
    </AppShell.Navbar>
  );
};
